import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Star, Upload } from "lucide-react";
import DatePickerField from "@/components/DatePickerField";

/** Renders a single custom field for the public audition signup form. */
export default function CustomFieldRenderer({ field, value, onChange }) {
  const { id, label, type, required, options, help_text } = field;
  const testid = `custom-${id}`;

  const wrap = (children) => (
    <div>
      <Label className="text-zinc-300 text-xs uppercase tracking-widest">
        {label}{required && <span className="text-gold ml-1">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
      {help_text && <div className="mt-1 text-[11px] text-zinc-500">{help_text}</div>}
    </div>
  );

  if (type === "text") return wrap(<Input data-testid={testid} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#121212] border-white/10 h-11" />);
  if (type === "textarea") return wrap(<Textarea data-testid={testid} required={required} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#121212] border-white/10" />);
  if (type === "number") return wrap(<Input data-testid={testid} type="number" required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#121212] border-white/10 h-11" />);
  if (type === "date") return wrap(<DatePickerField testid={testid} value={value || ""} onChange={onChange} placeholder="Pick date" />);
  if (type === "yesno") return wrap(
    <div className="flex items-center gap-2">
      {["Yes","No"].map((opt) => (
        <button key={opt} type="button" onClick={() => onChange(value === opt ? "" : opt)} data-testid={`${testid}-${opt.toLowerCase()}`}
          className={`h-10 px-4 rounded border text-sm ${value === opt ? "bg-gold text-black border-gold font-semibold" : "border-white/10 text-zinc-200 hover:bg-white/5"}`}>{opt}</button>
      ))}
    </div>
  );
  if (type === "select") return wrap(
    <div className="flex flex-wrap gap-1.5">
      {(options || []).map((o) => (
        <button key={o} type="button" onClick={() => onChange(value === o ? "" : o)} data-testid={`${testid}-${o}`}
          className={`text-xs px-3 py-1.5 rounded-full border ${value === o ? "bg-gold text-black border-gold font-semibold" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{o}</button>
      ))}
    </div>
  );
  if (type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    return wrap(
      <div className="flex flex-wrap gap-1.5">
        {(options || []).map((o) => {
          const on = arr.includes(o);
          return (
            <button key={o} type="button" onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])} data-testid={`${testid}-${o}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-gold text-black border-gold font-semibold" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{o}</button>
          );
        })}
      </div>
    );
  }
  if (type === "rating") return wrap(
    <div className="flex items-center gap-1">
      {[1,2,3,4,5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(value === n ? null : n)} data-testid={`${testid}-${n}`}
          className={`w-9 h-9 rounded border grid place-items-center ${value >= n ? "bg-gold/20 border-gold text-gold" : "border-white/10 text-zinc-500 hover:bg-white/5"}`}>
          <Star className={`w-4 h-4 ${value >= n ? "fill-current" : ""}`} />
        </button>
      ))}
    </div>
  );
  if (type === "file") {
    return wrap(
      <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-white/15 text-sm text-zinc-300 hover:bg-white/5 cursor-pointer">
        <Upload className="w-4 h-4" />
        <span>{value?.filename || value?.name || "Choose file"}</span>
        <input data-testid={testid} type="file" className="hidden" onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
    );
  }
  return null;
}
